import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { useSiteLogo } from '../../context/SiteLogoContext';
import { BRAND } from '../../config/brand';
import './Auth.css';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_RULES = 'At least 8 characters, one uppercase letter, one number, and one special character';

function validatePassword(password) {
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  if (!hasUpper || !hasNumber || !hasSpecial) {
    return PASSWORD_RULES;
  }
  return null;
}

function ResetPassword() {
  const { token } = useParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword, isAuthenticated } = useAuth();
  const { resolvedLogoUrl, logoAlt } = useSiteLogo();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    const result = await resetPassword(token, formData.password);

    if (result.success) {
      navigate('/login', { replace: true, state: { message: 'Password reset successful. Sign in with your new password.' } });
    } else {
      setError(result.message || 'Failed to reset password. This link may have expired — request a new one.');
    }

    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Reset Password — {BRAND.name}</title>
        <meta name="description" content={`Set a new password for your ${BRAND.name} account.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <img src={resolvedLogoUrl} alt={logoAlt || BRAND.name} className="auth-logo-image" />
            </Link>
            <h1 className="auth-title">Reset Password</h1>
            <p className="auth-subtitle">Enter your new password below</p>
          </div>

          {error && (
            <div className="auth-error" role="alert" aria-live="assertive">
              {error}
            </div>
          )}

          {token ? (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-form-group">
                <label htmlFor="password" className="auth-label">New Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="auth-input"
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH}
                  aria-describedby="password-hint"
                />
                <p id="password-hint" className="auth-hint">{PASSWORD_RULES}</p>
              </div>

              <div className="auth-form-group">
                <label htmlFor="confirmPassword" className="auth-label">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="auth-input"
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH}
                />
              </div>

              <button
                type="submit"
                className="auth-button auth-button--primary"
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>
            </form>
          ) : (
            <p className="auth-footer">
              <Link to="/forgot-password" className="auth-link auth-link--primary">
                Request a new reset link
              </Link>
            </p>
          )}

          <p className="auth-footer">
            Remember your password?{' '}
            <Link to="/login" className="auth-link auth-link--primary">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default ResetPassword;
